import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore,
  Add,
  Delete,
  Edit as EditIcon,
  Schedule,
  ContentCopy,
  CopyAll,
  UnfoldMore,
  UnfoldLess,
  WorkOutline,
} from "@mui/icons-material";
import { Shop, CoverageRequirement, WeeklyCoverage } from "../../types/shop.types";
import { DayOfWeek } from "../../types/common.types";
import TimePicker24 from "../common/TimePicker24";
import { startTimeToMinutes, endTimeToMinutes } from "../../utils/timeHelpers";

interface DefaultCoverageEditorProps {
  open: boolean;
  shop: Shop;
  shops: Shop[];
  onClose: () => void;
  onSave: (coverage: WeeklyCoverage) => Promise<void>;
  isLoading?: boolean;
}

const daysOfWeek: Array<{ key: DayOfWeek; label: string }> = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

export default function DefaultCoverageEditor({
  open,
  shop,
  shops,
  onClose,
  onSave,
  isLoading,
}: DefaultCoverageEditorProps) {
  const [coverage, setCoverage] = useState<WeeklyCoverage>({} as WeeklyCoverage);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | null>(null);
  const [selectedCoverage, setSelectedCoverage] = useState<CoverageRequirement | null>(
    null
  );
  const [coverageForm, setCoverageForm] = useState({
    startTime: "09:00",
    endTime: "17:00",
    minCoverage: 1,
  });
  const [timeError, setTimeError] = useState<string>("");
  const [copyDayDialogOpen, setCopyDayDialogOpen] = useState(false);
  const [copyFromShopDialogOpen, setCopyFromShopDialogOpen] = useState(false);
  const [coverageToCopy, setCoverageToCopy] = useState<{
    coverage: CoverageRequirement;
    fromDay: DayOfWeek;
  } | null>(null);
  const [selectedDaysToCopy, setSelectedDaysToCopy] = useState<DayOfWeek[]>([]);
  const [expandedDays, setExpandedDays] = useState<Record<DayOfWeek, boolean>>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  useEffect(() => {
    if (open) {
      // Initialize with shop's default coverage or empty arrays
      const initialCoverage: WeeklyCoverage = {} as WeeklyCoverage;
      const initialExpanded: Record<DayOfWeek, boolean> = {} as Record<DayOfWeek, boolean>;
      daysOfWeek.forEach(({ key }) => {
        initialCoverage[key] = shop.defaultCoverage?.[key] || [];
        initialExpanded[key] = (shop.defaultCoverage?.[key] || []).length > 0;
      });
      setCoverage(initialCoverage);
      setExpandedDays(initialExpanded);
    }
  }, [open, shop]);

  const handleSave = async () => {
    await onSave(coverage);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const handleAddCoverage = (day: DayOfWeek) => {
    setSelectedDay(day);
    setSelectedCoverage(null);
    const dayHours = shop.defaultHours[day];
    setCoverageForm({
      startTime: dayHours.openTime,
      endTime: dayHours.closeTime,
      minCoverage: 1,
    });
    setTimeError("");
    setEditDialogOpen(true);
  };

  const handleEditCoverage = (day: DayOfWeek, coverage: CoverageRequirement) => {
    setSelectedDay(day);
    setSelectedCoverage(coverage);
    setCoverageForm({
      startTime: coverage.startTime,
      endTime: coverage.endTime,
      minCoverage: coverage.minCoverage,
    });
    setTimeError("");
    setEditDialogOpen(true);
  };

  const handleDeleteCoverage = (day: DayOfWeek, shiftId: string) => {
    setCoverage({
      ...coverage,
      [day]: coverage[day].filter((s) => s.id !== shiftId),
    });
  };

  const handleCopyCoverage = (day: DayOfWeek, coverage: CoverageRequirement) => {
    setCoverageToCopy({ coverage, fromDay: day });
    setSelectedDaysToCopy([]);
    setCopyDayDialogOpen(true);
  };

  const handleToggleDayToCopy = (day: DayOfWeek) => {
    setSelectedDaysToCopy((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleApplyCopyToDays = () => {
    if (!coverageToCopy || selectedDaysToCopy.length === 0) return;

    const updatedCoverage = { ...coverage };
    selectedDaysToCopy.forEach((toDay) => {
      const copiedCoverage: CoverageRequirement = {
        ...coverageToCopy.coverage,
        id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      };
      updatedCoverage[toDay] = [...updatedCoverage[toDay], copiedCoverage];
    });

    setCoverage(updatedCoverage);
    setCopyDayDialogOpen(false);
    setCoverageToCopy(null);
    setSelectedDaysToCopy([]);
  };

  const handleExpandAll = () => {
    const allExpanded: Record<DayOfWeek, boolean> = {} as Record<DayOfWeek, boolean>;
    daysOfWeek.forEach(({ key }) => {
      allExpanded[key] = true;
    });
    setExpandedDays(allExpanded);
  };

  const handleCollapseAll = () => {
    const allCollapsed: Record<DayOfWeek, boolean> = {} as Record<DayOfWeek, boolean>;
    daysOfWeek.forEach(({ key }) => {
      allCollapsed[key] = false;
    });
    setExpandedDays(allCollapsed);
  };

  const handleAccordionChange = (day: DayOfWeek) => {
    setExpandedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleCopyFromShop = (sourceShopId: string) => {
    const sourceShop = shops.find((s) => s.id === sourceShopId);
    if (!sourceShop || !sourceShop.defaultCoverage) return;

    // Copy all coverage from source shop, generating new IDs
    const copiedCoverage: WeeklyCoverage = {} as WeeklyCoverage;
    daysOfWeek.forEach(({ key }) => {
      const sourceCoverage = sourceShop.defaultCoverage?.[key] || [];
      copiedCoverage[key] = sourceCoverage.map((coverage) => ({
        ...coverage,
        id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      }));
    });

    setCoverage(copiedCoverage);
    setCopyFromShopDialogOpen(false);
  };

  const validateCoverageTimes = (): boolean => {
    if (!selectedDay) return false;

    const dayHours = shop.defaultHours[selectedDay];
    if (!dayHours.isOpen) {
      setTimeError("Shop is closed on this day");
      return false;
    }

    // Use startTimeToMinutes for start times and endTimeToMinutes for end times
    // This handles overnight shops where close time like "00:30" means next day
    const startMinutes = startTimeToMinutes(coverageForm.startTime);
    const endMinutes = endTimeToMinutes(coverageForm.endTime);
    const openMinutes = startTimeToMinutes(dayHours.openTime);
    const closeMinutes = endTimeToMinutes(dayHours.closeTime);

    if (startMinutes < openMinutes || endMinutes > closeMinutes) {
      setTimeError(
        `Coverage must be within shop hours (${dayHours.openTime} - ${dayHours.closeTime})`
      );
      return false;
    }

    if (startMinutes >= endMinutes) {
      setTimeError("End time must be after start time");
      return false;
    }

    setTimeError("");
    return true;
  };

  const handleSaveCoverage = () => {
    if (!selectedDay || !validateCoverageTimes()) return;

    const newCoverage: CoverageRequirement = {
      id:
        selectedCoverage?.id ||
        `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      ...coverageForm,
    };

    if (selectedCoverage) {
      // Update existing coverage
      setCoverage({
        ...coverage,
        [selectedDay]: coverage[selectedDay].map((s) =>
          s.id === selectedCoverage.id ? newCoverage : s
        ),
      });
    } else {
      // Add new coverage
      setCoverage({
        ...coverage,
        [selectedDay]: [...coverage[selectedDay], newCoverage],
      });
    }

    setEditDialogOpen(false);
  };

  const getTotalCoverageCount = (): number => {
    return Object.values(coverage).reduce(
      (total, dayCoverage) => total + dayCoverage.length,
      0
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <WorkOutline color="primary" />
            <Typography variant="h5" fontWeight={600}>
              Default Coverage
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Set the default coverage requirements for <strong>{shop.name}</strong>
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {/* Quick Actions and Stats */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
              QUICK ACTIONS:
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={handleExpandAll}
              startIcon={<UnfoldMore fontSize="small" />}
              sx={{ minWidth: 'auto' }}
            >
              Expand All
            </Button>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="text"
              onClick={handleCollapseAll}
              startIcon={<UnfoldLess fontSize="small" />}
              sx={{ minWidth: 'auto' }}
            >
              Collapse All
            </Button>
            {shops.length > 1 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Tooltip
                  title={
                    !Object.values(shop.defaultHours).some((dayHours) => dayHours.isOpen)
                      ? "Set default hours first before copying coverage"
                      : ""
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCopyFromShopDialogOpen(true)}
                      disabled={
                        isLoading ||
                        !Object.values(shop.defaultHours).some((dayHours) => dayHours.isOpen)
                      }
                      startIcon={<CopyAll fontSize="small" />}
                      sx={{ minWidth: 'auto' }}
                    >
                      Copy from Shop
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip
                icon={<Schedule />}
                label={`${getTotalCoverageCount()} shifts configured`}
                size="small"
                color={getTotalCoverageCount() > 0 ? "primary" : "default"}
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {daysOfWeek.map(({ key, label }) => {
              const dayHours = shop.defaultHours[key];
              const dayCoverage = coverage[key] || [];

              return (
                <Accordion
                  key={key}
                  expanded={expandedDays[key]}
                  onChange={() => handleAccordionChange(key)}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s",
                    bgcolor: dayHours.isOpen ? "background.paper" : "grey.50",
                    borderLeft: dayHours.isOpen && dayCoverage.length > 0 ? 4 : 0,
                    borderLeftColor: "primary.main",
                    "&:before": {
                      display: "none",
                    },
                    "&:hover": {
                      boxShadow: 1,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        my: 1.5,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        sx={{
                          minWidth: 120,
                          color: dayHours.isOpen ? "primary.main" : "text.secondary",
                        }}
                      >
                        {label}
                      </Typography>
                      {dayHours.isOpen ? (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {dayHours.openTime} - {dayHours.closeTime}
                          </Typography>
                          <Chip
                            label={`${dayCoverage.length} shift${dayCoverage.length !== 1 ? 's' : ''}`}
                            size="small"
                            color={dayCoverage.length > 0 ? "primary" : "default"}
                            variant="outlined"
                            sx={{ ml: "auto" }}
                          />
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                          sx={{ ml: "auto" }}
                        >
                          Closed
                        </Typography>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {dayHours.isOpen ? (
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                      >
                        {dayCoverage.map((coverage) => (
                          <Paper
                            key={coverage.id}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              transition: "all 0.2s",
                              "&:hover": {
                                bgcolor: "grey.50",
                                boxShadow: 1,
                              },
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {coverage.startTime} - {coverage.endTime}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Min Coverage: {coverage.minCoverage} staff
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Tooltip title="Copy to another day">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyCoverage(key, coverage)}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCoverage(key, coverage)}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteCoverage(key, coverage.id)}
                                  color="error"
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Paper>
                        ))}
                        <Button
                          startIcon={<Add />}
                          onClick={() => handleAddCoverage(key)}
                          variant="outlined"
                          size="medium"
                          disabled={isLoading}
                          sx={{ borderRadius: 2 }}
                        >
                          Add Coverage
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Shop is closed on this day
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} size="large">
            Cancel
          </Button>
          <Tooltip
            title={
              !Object.values(shop.defaultHours).some((dayHours) => dayHours.isOpen)
                ? "Set default hours first before configuring coverage"
                : ""
            }
          >
            <span>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={
                  isLoading ||
                  !Object.values(shop.defaultHours).some((dayHours) => dayHours.isOpen)
                }
                size="large"
                sx={{ minWidth: 120 }}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Coverage Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <WorkOutline color="primary" />
            <Typography variant="h5" fontWeight={600}>
              {selectedCoverage ? "Edit Coverage" : "Add Coverage"}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {selectedDay && daysOfWeek.find((d) => d.key === selectedDay)?.label}
            {selectedDay && shop.defaultHours[selectedDay] && (
              <> • Shop hours: {shop.defaultHours[selectedDay].openTime} - {shop.defaultHours[selectedDay].closeTime}</>
            )}
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TimePicker24
              label="Start Time"
              value={coverageForm.startTime}
              onChange={(value) => {
                setCoverageForm({ ...coverageForm, startTime: value });
                setTimeError("");
              }}
              fullWidth
            />
            <TimePicker24
              label="End Time"
              value={coverageForm.endTime}
              onChange={(value) => {
                setCoverageForm({ ...coverageForm, endTime: value });
                setTimeError("");
              }}
              fullWidth
            />
            <TextField
              label="Minimum Coverage"
              type="number"
              value={coverageForm.minCoverage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCoverageForm({
                  ...coverageForm,
                  minCoverage: parseInt(e.target.value) || 1,
                })
              }
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Minimum number of staff required during this time"
            />
            {timeError && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: 'error.50',
                  border: '1px solid',
                  borderColor: 'error.200',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="error.main">
                  {timeError}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSaveCoverage}
            variant="contained"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy to Day Dialog */}
      <Dialog
        open={copyDayDialogOpen}
        onClose={() => setCopyDayDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ContentCopy color="primary" />
            <Typography variant="h5" fontWeight={600}>
              Copy Coverage to Days
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {coverageToCopy && (
              <>
                {coverageToCopy.coverage.startTime} - {coverageToCopy.coverage.endTime} from{" "}
                {daysOfWeek.find((d) => d.key === coverageToCopy.fromDay)?.label}
              </>
            )}
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            SELECT DAYS TO COPY TO:
          </Typography>
          <FormGroup>
            {daysOfWeek
              .filter((day) => shop.defaultHours[day.key].isOpen)
              .map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={selectedDaysToCopy.includes(key)}
                      onChange={() => handleToggleDayToCopy(key)}
                    />
                  }
                  label={label}
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    mx: -1.5,
                    borderRadius: 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'grey.50',
                    },
                  }}
                />
              ))}
          </FormGroup>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCopyDayDialogOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleApplyCopyToDays}
            variant="contained"
            disabled={selectedDaysToCopy.length === 0}
            size="large"
            sx={{ minWidth: 100 }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy from Shop Dialog */}
      <Dialog
        open={copyFromShopDialogOpen}
        onClose={() => setCopyFromShopDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: 'primary.50',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CopyAll color="primary" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Copy Coverage from Shop
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select a shop to copy all coverage
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {shops
              .filter((s) => s.id !== shop.id && s.defaultCoverage)
              .map((sourceShop) => {
                const shiftsCount = Object.values(
                  sourceShop.defaultCoverage || {}
                ).reduce((total, dayCoverage) => total + dayCoverage.length, 0);
                return (
                  <Paper
                    key={sourceShop.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50',
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleCopyFromShop(sourceShop.id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: sourceShop.color || 'primary.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <WorkOutline sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {sourceShop.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {shiftsCount} shift{shiftsCount !== 1 ? 's' : ''} configured
                        </Typography>
                      </Box>
                      <ContentCopy sx={{ color: 'action.active', fontSize: 20 }} />
                    </Box>
                  </Paper>
                );
              })}
            {shops.filter((s) => s.id !== shop.id && s.defaultCoverage).length === 0 && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No other shops have coverage configured yet.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Configure coverage for other shops to use this feature.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCopyFromShopDialogOpen(false)} size="large">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
